import React, { useEffect } from "react";
import styled from "styled-components";
import { T, FONTS, FS, COLORS } from "../../HearingBundles/tokens/index.js";
import { Icon } from "../components/Icons";
import { Badge, IconBtn } from "../components/Primitives";
import UnassignedDocRow from "../components/UnassignedDocRow.js";
import { getPageCount } from "../CreateTrialBundleModal";

const pagesToRangeString = (pageSet) => {
  if (!pageSet || pageSet.size === 0) return "";
  const pages = Array.from(pageSet).sort((a, b) => a - b);
  if (pages.length === 1) return String(pages[0]);

  const ranges = [];
  let start = pages[0];
  let prev = pages[0];

  for (let i = 1; i < pages.length; i++) {
    if (pages[i] !== prev + 1) {
      ranges.push(start === prev ? String(start) : `${start}-${prev}`);
      start = pages[i];
    }
    prev = pages[i];
  }
  ranges.push(start === prev ? String(start) : `${start}-${prev}`);
  return ranges.join(",");
};

const getNextGlobalTabNum = (bundles) => {
  let maxTab = 0;
  bundles.forEach((bundle) => {
    bundle.assignments.forEach((a) => {
      if (a.tabNum > maxTab) maxTab = a.tabNum;
    });
  });
  return maxTab + 1;
};

const Step3OrganiseBundles = ({
  documents,
  pickedPages,
  bundles,
  setBundles,
  onAutoAssign,
  checked,
}) => {
  const pickedDocIds = Object.keys(pickedPages).filter(
    (id) => pickedPages[id]?.size > 0,
  );

  const findDoc = (id) => documents.find((d) => String(d.id) === String(id));

  useEffect(() => {
    if (!checked && pickedDocIds.length > 0) {
      const timer = setTimeout(() => {
        onAutoAssign();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [checked, pickedDocIds.length, onAutoAssign]);

  const assignDoc = (docId, targetCode) => {
    setBundles((prev) => {
      const tgtBundle = prev.find((b) => b.code === targetCode);
      if (!tgtBundle) return prev;

      const alreadyIn = tgtBundle.assignments.some(
        (a) => String(a.sourceDocId) === String(docId),
      );
      if (alreadyIn) return prev;

      const tabNum = checked
        ? getNextGlobalTabNum(prev)
        : (() => {
            const usedTabs = new Set(
              tgtBundle.assignments.map((a) => a.tabNum),
            );
            let t = 1;
            while (usedTabs.has(t)) t++;
            return t;
          })();

      const pageSet = pickedPages[docId];
      const pagesStr = pagesToRangeString(pageSet);
      const doc = findDoc(docId);

      const newAssignment = {
        sourceDocId: docId,
        id: doc?.id ?? docId,
        fileUrl: doc?.fileUrl ?? "",
        tabNum,
        userLabel: doc ? doc.name : `Document ${docId}`,
        pages: pagesStr || "1",
        visibilityType: "me",
      };

      return prev.map((b) =>
        b.code === targetCode
          ? { ...b, assignments: [...b.assignments, newAssignment] }
          : b,
      );
    });
  };

  const unassignDoc = (docId, bundleCode) => {
    setBundles((prev) =>
      prev.map((bundle) =>
        bundle.code !== bundleCode
          ? bundle
          : {
              ...bundle,
              assignments: bundle.assignments.filter(
                (a) => String(a.sourceDocId) !== String(docId),
              ),
            },
      ),
    );
  };

  const assignedDocIds = new Set(
    bundles.flatMap((b) => b.assignments.map((a) => String(a.sourceDocId))),
  );

  const unassignedDocIds = pickedDocIds.filter(
    (id) => !assignedDocIds.has(String(id)),
  );

  const totalAssignedPages = bundles.reduce(
    (total, bundle) =>
      total +
      (bundle.assignments || []).reduce(
        (sum, a) => sum + getPageCount(a.pages),
        0,
      ),
    0,
  );

  const flatBundle = bundles[0];

  if (!checked) {
    return (
      <Container>
        <TopBar>
          <TopTitle>Assign Documents to Bundle</TopTitle>
        </TopBar>

        <InfoCallout>
          <Icon.Info size={14} color={T.navy} style={{ marginTop: 2 }} />
          <span>
            No sub-folders selected. All documents are automatically placed
            directly into the bundle.{" "}
            <strong>Each document gets its own tab.</strong>
          </span>
        </InfoCallout>

        {unassignedDocIds.length > 0 && (
          <WarnBox>
            <WarnTitle>
              UNASSIGNED DOCUMENTS ({unassignedDocIds.length}) — assigning
              automatically...
            </WarnTitle>
            {unassignedDocIds.map((id) => {
              const doc = findDoc(id);
              if (!doc) return null;
              const pgCount = pickedPages[id]?.size || 0;
              return (
                <UnassignedDocRow
                  key={id}
                  doc={doc}
                  pgCount={pgCount}
                  bundles={flatBundle ? [flatBundle] : []}
                  onAssign={(targetCode) => assignDoc(id, targetCode)}
                />
              );
            })}
          </WarnBox>
        )}

        {unassignedDocIds.length === 0 && totalAssignedPages > 0 && (
          <AllAssignedBanner>
            <Icon.Check size={14} color={COLORS.BTN_GREEN} />
            All selected documents have been automatically assigned
          </AllAssignedBanner>
        )}

        {flatBundle && (
          <BundleCard>
            <BundleCardHeader>
              <Icon.FolderOpen size={16} color={T.tealDark} />
              <BundleCardTitle>Bundle</BundleCardTitle>
              <Badge $bg={T.tealTint} $fg={T.tealDark}>
                {flatBundle.assignments.length} document
                {flatBundle.assignments.length !== 1 ? "s" : ""}
              </Badge>
              <Badge $bg={T.white} $fg={T.ink}>
                {totalAssignedPages} page{totalAssignedPages !== 1 ? "s" : ""}
              </Badge>
            </BundleCardHeader>

            <BundleCardBody>
              {flatBundle.assignments.length === 0 ? (
                <EmptyBundle>No documents assigned yet.</EmptyBundle>
              ) : (
                flatBundle.assignments
                  .sort((a, b) => a.tabNum - b.tabNum)
                  .map((assignment) => {
                    const pgCount = getPageCount(assignment.pages);
                    return (
                      <TabCard key={assignment.tabNum}>
                        <TabCardHeader>
                          <TabLabel>TAB {assignment.tabNum}</TabLabel>
                          <TabDocName title={assignment.userLabel}>
                            {assignment.userLabel}
                          </TabDocName>
                          <TabMeta>
                            {pgCount} page{pgCount !== 1 ? "s" : ""}
                          </TabMeta>
                          <IconBtn
                            onClick={() =>
                              unassignDoc(
                                assignment.sourceDocId,
                                flatBundle.code,
                              )
                            }
                            title="Remove"
                            style={{ marginLeft: "auto" }}
                          >
                            <Icon.X size={13} color={T.red} />
                          </IconBtn>
                        </TabCardHeader>
                      </TabCard>
                    );
                  })
              )}
            </BundleCardBody>
          </BundleCard>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <TopBar>
        <TopTitle>Organise into Sub-Folders &amp; Tabs</TopTitle>
        <Stats>
          <Badge $bg={T.tealTint} $fg={T.tealDark}>
            {totalAssignedPages} pages assigned
          </Badge>
          <Badge $bg="#FEF3C7" $fg="#92400E">
            {pickedDocIds.length} document
            {pickedDocIds.length !== 1 ? "s" : ""}
          </Badge>
        </Stats>
      </TopBar>

      {/* <InfoCallout>
        <Icon.Info size={14} color={T.navy} style={{ marginTop: 2 }} />
        <span>
          Each <strong>document</strong> must be assigned to a sub-folder. Tabs
          are numbered <strong>continuously</strong> across all sub-folders.
        </span>
      </InfoCallout> */}

      {unassignedDocIds.length > 0 ? (
        <WarnBox>
          <WarnTitle>
            UNASSIGNED DOCUMENTS ({unassignedDocIds.length}) — optional
          </WarnTitle>
          <WarnSubTitle>
            These documents are not assigned to any sub-folder. You can assign
            them below, or leave them — they will still be included in the
            bundle without a sub-folder prefix.
          </WarnSubTitle>
          {unassignedDocIds.map((id) => {
            const doc = findDoc(id);
            if (!doc) return null;
            const pgCount = pickedPages[id]?.size || 0;
            return (
              <UnassignedDocRow
                key={id}
                doc={doc}
                pgCount={pgCount}
                bundles={bundles}
                onAssign={(targetCode) => assignDoc(id, targetCode)}
              />
            );
          })}
        </WarnBox>
      ) : (
        <AllAssignedBanner>
          <Icon.Check size={14} color={COLORS.BTN_GREEN} />
          All selected documents have been assigned to sub-folders
        </AllAssignedBanner>
      )}

      <ScrollContainer>
        {bundles.map((b) => {
          const byTab = {};
          b.assignments.forEach((a) => {
            if (!byTab[a.tabNum]) byTab[a.tabNum] = [];
            byTab[a.tabNum].push(a);
          });

          const tabNums = Object.keys(byTab)
            .map(Number)
            .sort((a, c) => a - c);

          const bundlePageCount = (b.assignments || []).reduce(
            (sum, a) => sum + getPageCount(a.pages),
            0,
          );

          return (
            <BundleCard key={b.code}>
              <BundleCardHeader>
                <Icon.FolderOpen size={16} color={T.tealDark} />
                <BundleCardTitle>
                  [{b.prefix || b.code}] {b.name}
                </BundleCardTitle>
                <Badge $bg={T.white} $fg={T.ink}>
                  {b.assignments.length} document
                  {b.assignments.length !== 1 ? "s" : ""}
                </Badge>
                <Badge $bg={T.white} $fg={T.ink}>
                  {bundlePageCount} page{bundlePageCount !== 1 ? "s" : ""}
                </Badge>
                <Badge $bg={T.white} $fg={T.ink}>
                  {tabNums.length} tab{tabNums.length !== 1 ? "s" : ""}
                </Badge>
              </BundleCardHeader>

              <BundleCardBody>
                {tabNums.length === 0 ? (
                  <EmptyBundle>No documents assigned yet.</EmptyBundle>
                ) : (
                  tabNums.map((sNum) => {
                    const assignment = byTab[sNum][0];
                    const pgCount = getPageCount(assignment?.pages || "");
                    return (
                      <TabCard key={sNum}>
                        <TabCardHeader>
                          <TabLabel>TAB {sNum}</TabLabel>
                          <TabDocName title={assignment?.userLabel}>
                            {assignment?.userLabel || "—"}
                          </TabDocName>
                          <TabMeta>
                            {pgCount} page{pgCount !== 1 ? "s" : ""}
                          </TabMeta>
                          <IconBtn
                            onClick={() =>
                              unassignDoc(assignment?.sourceDocId, b.code)
                            }
                            title="Remove this document"
                            style={{ marginLeft: "auto" }}
                          >
                            <Icon.X size={13} color={T.red} />
                          </IconBtn>
                        </TabCardHeader>
                      </TabCard>
                    );
                  })
                )}
              </BundleCardBody>
            </BundleCard>
          );
        })}
      </ScrollContainer>
    </Container>
  );
};

export default Step3OrganiseBundles;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${T.line};
`;

const TopTitle = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.lg};
  font-weight: 700;
  color: ${T.navy};
`;

const Stats = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const InfoCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${T.navyTint};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.navy};
  line-height: 1.5;
  border-left: 4px solid ${T.teal};
`;

const WarnBox = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1.5px dashed ${T.warnBorder};
  background: ${T.warnTint};
`;

const WarnTitle = styled.div`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: ${T.warn};
  margin-bottom: 10px;
`;

const AllAssignedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f0fdf4;
  border: 1.5px solid #86efac;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: #15803d;
`;

const ScrollContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${T.line};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${T.mute};
  }

  @media (max-width: 768px) {
    max-height: 48vh;
    min-height: 250px;
  }
`;

const BundleCard = styled.div`
  border: 1.5px solid ${T.line};
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const BundleCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  background: ${T.soft};
  border-bottom: 1px solid ${T.line};
  flex-wrap: wrap;
`;

const BundleCardTitle = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.md};
  font-weight: 700;
  color: ${T.navy};
  flex: 1;
`;

const BundleCardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyBundle = styled.div`
  padding: 32px 20px;
  text-align: center;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.mute};
`;

const TabCard = styled.div`
  border: 1px solid ${T.line};
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const TabCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.sm};
  color: ${T.mute};
`;

const TabLabel = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  color: ${T.tealDark};
  background: ${T.tealTint};
  padding: 2px 8px;
  border-radius: 5px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TabDocName = styled.span`
  flex: 1;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: ${T.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TabMeta = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  white-space: nowrap;
  flex-shrink: 0;
`;

const WarnSubTitle = styled.div`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.warn};
  margin-bottom: 10px;
  line-height: 1.5;
`;
